import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Link from '@/components/utilities/link/link';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import styles from './image-scroll-over-card.module.scss';

export interface ImageScrollOverCardProps {
  /** The text to show as a caption */
  caption?: Content.Components['ImageScrollOverCardDto']['caption'];
  /** The text to show as a description */
  description?: Content.Components['ImageScrollOverCardDto']['description'];
  /** The text to show as a headline */
  headline?: Content.Components['ImageScrollOverCardDto']['headline'];
  /** The image to show */
  image?: Content.Components['ImageScrollOverCardDto']['image'];
  /** The link to show */
  link?: Content.Components['ImageScrollOverCardDto']['link'];
}

const ImageScrollOverCard = ({
  caption,
  description,
  headline,
  image,
  link,
}: ImageScrollOverCardProps) => {
  const { bindRef, isHovered } = useIsHovered<HTMLAnchorElement>();

  return (
    <Link
      ref={bindRef}
      className={styles.component}
      target={link?.target}
      url={link?.url}
    >
      {caption && (
        <div className={styles.caption}>
          <Typography tagStyle="labelMedium">{caption}</Typography>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.image}>
          {image && (
            <Image
              alt={image.altText}
              aspectRatio="threeByFour"
              focalPoint={image.focalPoint}
              height={416}
              src={image.url}
              unoptimized
              width={312}
            />
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.contentLeft}>
            {headline && (
              <div className={styles.headline}>
                <Typography tagStyle="bodyLargeBold">{headline}</Typography>
              </div>
            )}

            {description && (
              <div className={styles.description}>
                <RichText text={description} />
              </div>
            )}
          </div>

          <div className={styles.contentRight}>
            <Button hovered={isHovered} icon="ChevronRight" />
          </div>
        </div>
      </div>
    </Link>
  );
};

ImageScrollOverCard.displayName = 'ImageScrollOverCard';

export default ImageScrollOverCard;
